import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Tab, Tabs, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import LocationContent from './LocationContent';
import AssignedAttendeesContent from './AssignedAttendeesContent';
import UnAssignedAttendeesContent from './UnAssignedAttendeesContent';
import DashboardContent from './DashboardContent';
import ButtonDropdown from '@ui/components/ButtonDropdown';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { getPickupLocationsNotPaginatedByEventIdAsync } from '@application/features/pickupLocation/pickupLocationThunk';
import { FamilyFestGetPickupLocationsNotPaginatedAsync } from '@application/features/familyFest/familyFestPickupLocationThunk';
import { CollaboratorAccessLevel } from '@application/enums/collaborator-access-level';
import { useSelector } from 'react-redux';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import ParkingContent from './ParkingContent';
import RoutesContent from './RoutesContent';
import VehiclesContent from './VehiclesContent';
import { Lock1 } from 'iconsax-react';
import { toast } from 'react-toastify';
import { generatePassportOtpAsync, verifyPassportOtpAsync } from '@application/features/familyFest/familyFestPassportThunk';
import { confirmNda, resetVerification } from '@application/features/familyFest/familyFestPassportSlice';
import { APIStatus } from '@application/enums/api-status';
import PassportContent, { PassportNonDisclosureDialog, PassportOtpAuthenticationDialog, PassportRestrictedAccessDialog } from './PassportContent';
import AuditLogContent from './AuditLogContent';
const TransportTab = () => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { eventId } = useParams();
    const event = useAppSelector((state) => state.VDKEvent.VDKEventsForEventDetails);
    const isFamilyFest = event?.type === EventTypeEnum.Family;
    const { pickupLocationsList } = useAppSelector((state) => state.pickupLocation);
    const { familyFestPickupLocationsList } = useAppSelector((state) => state.familyFestPickupLocation);
    // The "All Locations" dropdown options are scoped by the selected companies via the auth
    // token, so they must be refetched whenever the sidebar company selection changes.
    const selectedCompanyIds = useAppSelector((state) => state.company.selectedCompanyId);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [lastActiveTab, setLastActiveTab] = useState('dashboard');
    const [passportAccessDialogOpen, setPassportAccessDialogOpen] = useState(false);
    const [passportOtpDialogOpen, setPassportOtpDialogOpen] = useState(false);
    const [passportNonDisclosureDialogOpen, setPassportNonDisclosureDialogOpen] = useState(false);
    const [passportVerified, setPassportVerified] = useState(false);
    const { status: passportOtpStatus, verifyStatus: passportVerifyStatus } = useAppSelector((state) => state.familyFestPassport);
    const otpGenerating = passportOtpStatus === APIStatus.PENDING;
    const otpVerifying = passportVerifyStatus === APIStatus.PENDING;
    const [selectedLocationId, setSelectedLocationId] = useState('all');
    const currentUser = useSelector((state) => state.auth.currentUser);
    const isTransportManager = Array.isArray(currentUser?.role) && currentUser?.role.includes('TransportManager');
    const isExternalTransportManager = isTransportManager &&
        Number(currentUser?.CollaboratorAccessLevel) === CollaboratorAccessLevel.External;
    const isHappinessManager = Array.isArray(currentUser?.role) && currentUser?.role.includes('HappinessManager');
    const restrictedTabsForExternalTransportManager = ['assigned', 'unassigned'];
    const baseTabs = [
        { label: t('events.eventDetails.transportTab.dashboard'), value: 'dashboard' },
        { label: t('events.eventDetails.transportTab.locations'), value: 'locations' },
        ...(isFamilyFest
            ? [
                {
                    label: t('events.eventDetails.transportTab.vehicles'),
                    value: 'vehicles'
                },
            ]
            : []),
        { label: t('events.eventDetails.transportTab.assignedAttendees'), value: 'assigned' },
        { label: t('events.eventDetails.transportTab.unassignedAttendees'), value: 'unassigned' },
        // Show 'parkings' tab only if isFamilyFest is true
        ...(isFamilyFest
            ? [
                {
                    label: t('events.eventDetails.transportTab.parkings'),
                    value: 'parkings'
                },
                {
                    label: t('events.eventDetails.routeTab.routes'),
                    value: 'routes'
                },
                {
                    label: (_jsxs(Box, { display: 'flex', alignItems: 'center', gap: 0.75, children: [_jsx(Typography, { variant: 'inherit', children: t('events.eventDetails.passportTab.passport') }), !passportVerified && _jsx(Lock1, { variant: 'Outline', size: 20 })] })),
                    value: 'passport'
                },
                {
                    label: t('events.eventDetails.auditLogTab.auditLogs'),
                    value: 'auditLog'
                }
            ]
            : [])
    ];
    const tabLabels = baseTabs.filter((tab) => {
        if (isExternalTransportManager) {
            return !restrictedTabsForExternalTransportManager.includes(tab.value);
        }
        if (!isHappinessManager) {
            return tab.value !== 'auditLog' && (isTransportManager || tab.value !== 'passport');
        }
        return true;
    });
    const handleTabChange = (_event, newValue) => {
        if (activeTab === 'passport' && newValue !== 'passport') {
            dispatch(resetVerification());
            setPassportVerified(false);
        }
        if (newValue === 'passport') {
            if (passportVerified) {
                setActiveTab('passport');
                return;
            }
            setLastActiveTab(activeTab);
            setActiveTab('passport');
            setPassportAccessDialogOpen(true);
            return;
        }
        setActiveTab(newValue);
    };
    useEffect(() => {
        return () => {
            dispatch(resetVerification());
        };
    }, [dispatch]);
    useEffect(() => {
        if (!tabLabels.some((tab) => tab.value === activeTab)) {
            setActiveTab('dashboard');
        }
    }, [tabLabels, activeTab]);
    useEffect(() => {
        if (!eventId)
            return;
        if (isFamilyFest) {
            if (activeTab !== 'dashboard')
                return;
            dispatch(FamilyFestGetPickupLocationsNotPaginatedAsync({
                includeClosed: false
            }));
        }
        else {
            dispatch(getPickupLocationsNotPaginatedByEventIdAsync({
                eventId,
                includeClosed: false
            }));
        }
    }, [dispatch, eventId, isFamilyFest, activeTab, selectedCompanyIds]);
    const locationOptions = useMemo(() => {
        const base = [
            {
                id: 'all',
                text: t('events.eventDetails.transportTab.allLocations'),
                to: () => { }
            }
        ];
        const list = isFamilyFest ? familyFestPickupLocationsList : pickupLocationsList;
        const dynamic = (list ?? []).map((location) => ({
            id: location.id,
            text: location.name,
            to: () => { }
        }));
        return [...base, ...dynamic];
    }, [pickupLocationsList, familyFestPickupLocationsList, isFamilyFest, t]);
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: 3, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(Box, { sx: {
                            flex: isSmallScreen ? 1 : 'unset',
                            minWidth: 0,
                            maxWidth: '88%'
                        }, children: _jsx(Tabs, { variant: 'scrollable', scrollButtons: 'auto', allowScrollButtonsMobile: true, value: activeTab, onChange: handleTabChange, "aria-label": 'transport tabs', sx: {
                                width: isSmallScreen ? '100%' : 'fit-content',
                                maxWidth: '100%',
                                '& .MuiTab-root': {
                                    minHeight: 48,
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    fontWeight: 500
                                },
                                '& .MuiTab-root:not(.Mui-selected)': {
                                    color: theme.palette.text.primary
                                },
                                '& .MuiTab-root.Mui-selected': {
                                    color: theme.palette.text.primary
                                }
                            }, children: tabLabels.map((tab) => (_jsx(Tab, { label: tab.label, value: tab.value }, tab.value))) }) }), activeTab === 'dashboard' && (_jsx(ButtonDropdown, { label: locationOptions.find((x) => x.id === selectedLocationId)?.text ??
                            t('events.eventDetails.transportTab.allLocations'), keepOriginalOrder: true, variant: 'outlined', color: 'primary', options: locationOptions, selectedItem: selectedLocationId, onSelectionChange: (id) => setSelectedLocationId(id) }))] }), activeTab === 'dashboard' && (_jsx(DashboardContent, { selectedLocationId: selectedLocationId === 'all' ? undefined : selectedLocationId })), activeTab === 'locations' && _jsx(LocationContent, {}), activeTab === 'vehicles' && _jsx(VehiclesContent, {}), activeTab === 'assigned' && _jsx(AssignedAttendeesContent, {}), activeTab === 'unassigned' && _jsx(UnAssignedAttendeesContent, {}), activeTab === 'parkings' && _jsx(ParkingContent, {}), activeTab === 'routes' && isFamilyFest && (_jsx(RoutesContent, { onGoToLocationsTab: () => setActiveTab('locations') })), activeTab === 'passport' && isFamilyFest && _jsx(PassportContent, {}), activeTab === 'auditLog' && isFamilyFest && _jsx(AuditLogContent, {}), _jsx(PassportRestrictedAccessDialog, { open: passportAccessDialogOpen, loading: otpGenerating, onClose: () => {
                    setPassportAccessDialogOpen(false);
                    setActiveTab(lastActiveTab);
                }, onContinue: () => {
                    dispatch(generatePassportOtpAsync(eventId))
                        .unwrap()
                        .then((response) => {
                        if (response.isSuccess) {
                            setPassportAccessDialogOpen(false);
                            setPassportOtpDialogOpen(true);
                        }
                        else {
                            toast.error(response.message);
                        }
                    })
                        .catch((error) => {
                        const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                            ? error.message
                            : undefined;
                        toast.error(errorMessage);
                    });
                } }), _jsx(PassportOtpAuthenticationDialog, { open: passportOtpDialogOpen, loading: otpVerifying, onClose: () => {
                    setPassportOtpDialogOpen(false);
                    setActiveTab(lastActiveTab);
                }, onVerify: (otp) => {
                    dispatch(verifyPassportOtpAsync(otp))
                        .unwrap()
                        .then((response) => {
                        if (response.isSuccess) {
                            setPassportOtpDialogOpen(false);
                            setPassportNonDisclosureDialogOpen(true);
                        }
                        else {
                            toast.error(response.message);
                        }
                    })
                        .catch((error) => {
                        const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                            ? error.message
                            : undefined;
                        toast.error(errorMessage);
                    });
                }, onResend: () => {
                    dispatch(generatePassportOtpAsync(eventId))
                        .unwrap()
                        .then((response) => {
                        if (!response.isSuccess) {
                            toast.error(response.message);
                        }
                    })
                        .catch((error) => {
                        const errorMessage = typeof error === 'object' && error !== null && 'message' in error
                            ? error.message
                            : undefined;
                        toast.error(errorMessage);
                    });
                } }), _jsx(PassportNonDisclosureDialog, { open: passportNonDisclosureDialogOpen, onClose: () => {
                    setPassportNonDisclosureDialogOpen(false);
                    setActiveTab(lastActiveTab);
                }, onContinue: () => {
                    setPassportNonDisclosureDialogOpen(false);
                    setPassportVerified(true);
                    dispatch(confirmNda());
                } })] }));
};
export default TransportTab;
